import React, { useEffect, useState } from 'react';
import BootstrapTable from 'react-bootstrap-table-next';
import * as S from '../Advances/AdvancesStyles';
import { getPollinationProject, getPollinationProjectFarmers } from '../../../../services/pollinationService';
import FarmerDetailDrawer from './FarmerDetailDrawer';
import PhotoLightbox from './PhotoLightbox';

const STATUS_VARIANT = {
  INTERESTED: 'default',
  CONSENTED: 'advance',
  BASELINE_DONE: 'pending',
  HIVES_DEPLOYED: 'approved',
  OBSERVING: 'approved',
  HARVEST_LOGGED: 'settled',
};
const STATUS_LABEL = {
  INTERESTED: 'Interested',
  CONSENTED: 'Consented',
  BASELINE_DONE: 'Baseline done',
  HIVES_DEPLOYED: 'Hives deployed',
  OBSERVING: 'Observing',
  HARVEST_LOGGED: 'Harvest logged',
};

// Read-only — same farmer lifecycle data the mobile app writes, viewed here,
// never edited from the web. match/history come through as props via
// DefaultAppLayout, same convention as ProjectDetails.js.
const PollinationFarmersTable = ({ match, history }) => {
  const { projectId } = match.params;

  const [project, setProject] = useState(null);
  const [farmers, setFarmers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFarmerId, setSelectedFarmerId] = useState(null);
  const [consentUrl, setConsentUrl] = useState(null);

  const load = async () => {
    setLoading(true);
    const [proj, farms] = await Promise.all([
      getPollinationProject(projectId).catch(() => null),
      getPollinationProjectFarmers(projectId),
    ]);
    setProject(proj);
    setFarmers(farms);
    setLoading(false);
  };

  useEffect(() => { load(); }, [projectId]);

  const columns = [
    {
      dataField: 'name',
      text: 'Farmer',
      sort: true,
      formatter: (name, row) => (
        <span style={{ color: '#2563EB', cursor: 'pointer', fontWeight: 600 }} onClick={() => setSelectedFarmerId(row._id)}>
          {name}
        </span>
      ),
    },
    { dataField: 'mobile', text: 'Mobile', formatter: (v) => v || '—' },
    {
      dataField: 'village',
      text: 'Location',
      formatter: (v, row) => [row.village, row.block].filter(Boolean).join(', Block ') || '—',
    },
    {
      dataField: 'status',
      text: 'Status',
      formatter: (v, row) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <S.Pill variant={STATUS_VARIANT[v]}>{STATUS_LABEL[v] || v}</S.Pill>
          {row.consent_form_url && /^https?:\/\//i.test(row.consent_form_url) && (
            <span
              role="button"
              style={{ color: '#2563EB', cursor: 'pointer', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}
              onClick={() => setConsentUrl(row.consent_form_url)}
            >
              View consent form
            </span>
          )}
        </span>
      ),
    },
  ];

  return (
    <S.MainContainer>
      <S.TopBar>
        <div>
          <S.Btn ghost sm onClick={() => history.push('/pollination-projects')} style={{ marginBottom: 10 }}>← All projects</S.Btn>
          <h1>{project?.name || 'Project farmers'}</h1>
          <p>{[project?.location?.district, project?.location?.state].filter(Boolean).join(', ') || project?.key_crop}</p>
        </div>
      </S.TopBar>

      <S.TableWrap>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#6B7280' }}>Loading…</div>
        ) : (
          <BootstrapTable
            keyField="_id"
            data={farmers}
            columns={columns}
            bordered={false}
            hover
            noDataIndication={() => (
              <div style={{ textAlign: 'center', padding: 40, color: '#6B7280' }}>
                No farmers registered under this project yet.
              </div>
            )}
          />
        )}
      </S.TableWrap>

      <PhotoLightbox url={consentUrl} onClose={() => setConsentUrl(null)} alt="Consent form" />

      <FarmerDetailDrawer
        show={!!selectedFarmerId}
        farmerId={selectedFarmerId}
        onClose={() => setSelectedFarmerId(null)}
      />
    </S.MainContainer>
  );
};

export default PollinationFarmersTable;
